import React, { useState, useEffect } from 'react';
import { 
  FiFlag, 
  FiAlertTriangle, 
  FiCheckCircle, 
  FiEye, 
  FiTrash2, 
  FiChevronRight,
  FiLoader,
  FiInfo
} from 'react-icons/fi';
import adminReportService from '../services/adminReportService';
import adminProjectService from '../services/adminProjectService';
import { toast } from 'react-hot-toast';

const ReportsPage = () => {
  const [reportedProjects, setReportedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectReports, setProjectReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState(null); 
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchReportedProjects();
  }, []);

  const fetchReportedProjects = async () => {
    try {
      setLoading(true);
      const response = await adminReportService.getGroupedReports();
      setReportedProjects(response.data || []);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch reported projects');
    } finally {
      // Wait for a bit to show loading state
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleViewReports = async (project) => {
    setSelectedProject(project);
    setLoadingReports(true);
    setIsModalOpen(true);
    
    try {
      const response = await adminReportService.getProjectReports(project.id);
      setProjectReports(response.data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch reports');
      setIsModalOpen(false);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleDismissReports = async (projectId) => {
    if (!window.confirm('Are you sure you want to dismiss all reports for this project? This will mark them as resolved.')) {
      return;
    }

    try {
      setProcessing(true);
      await adminReportService.dismissReports(projectId);
      toast.success('Reports dismissed successfully');
      setIsModalOpen(false);
      fetchReportedProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to dismiss reports');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to reactivate this project? It will be visible to the public again.')) {
      return;
    }

    try {
      setProcessing(true);
      await adminProjectService.updateProjectStatus(projectId, 'active');
      toast.success('Project reactivated successfully');
      setIsModalOpen(false);
      fetchReportedProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to reactivate project');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspendProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to hide this project from the public?')) {
      return;
    }

    try {
      setProcessing(true);
      await adminProjectService.updateProjectStatus(projectId, 'hidden');
      toast.success('Project hidden successfully');
      setIsModalOpen(false);
      fetchReportedProjects();
    } catch (error) {
      toast.error(error.message || 'Failed to hide project');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <FiLoader className="h-8 w-8 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-500">Loading reported projects...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Reports</h1>
          <p className="text-gray-500 mt-1">Review and manage reported projects</p>
        </div>
        <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
          <FiFlag className="text-blue-600" />
          <span className="text-blue-700 font-medium">{reportedProjects.length} Flagged Projects</span>
        </div>
      </div>

      {/* Admin Instruction Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-md overflow-hidden text-white">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <FiInfo className="h-6 w-6 text-blue-200" />
            <h2 className="text-lg font-bold">Admin Resolution Guide</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-semibold text-blue-100">
                <span className="w-6 h-6 rounded-full bg-blue-500/50 flex items-center justify-center text-xs">1</span>
                Review Reports
              </div>
              <p className="text-sm text-blue-50">Click the eye icon to see detailed complaints. Verify if the claims are valid before taking action.</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-semibold text-blue-100">
                <span className="w-6 h-6 rounded-full bg-blue-500/50 flex items-center justify-center text-xs">2</span>
                Dismissal
              </div>
              <p className="text-sm text-blue-50">Use 'Dismiss Reports' if the complaints are unfounded. This clears the report flags but keeps the project status as-is.</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-semibold text-blue-100">
                <span className="w-6 h-6 rounded-full bg-blue-500/50 flex items-center justify-center text-xs">3</span>
                Reactivate / Hide
              </div>
              <p className="text-sm text-blue-50">Use 'Hide' for malicious projects. Use 'Reactivate' to restore a suspended project to the public view after review.</p>
            </div>
          </div>
        </div>
      </div>

      {reportedProjects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="h-16 w-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No active reports</h3>
          <p className="text-gray-500 mt-2 max-w-sm mx-auto">
            Everything looks good! There are currently no projects with pending reports.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reportedProjects.map((item) => (
            <div 
              key={item.id}
              className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start space-x-4">
                  <div className="h-16 w-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {item.cover_image ? (
                      <img src={item.cover_image} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-gray-400">
                        <FiFlag />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${adminProjectService.getStatusBadgeColor(item.status)}`}>
                        {item.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{item.description}</p>
                    <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-gray-400 font-medium">Owner: {item.owner?.full_name || item.owner?.pi_username || 'Unknown'}</span>
                        <span className="text-xs text-gray-400">Created: {new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className={`px-4 py-2 rounded-lg text-center ${adminReportService.getReportStatusColor(item._count.reports)}`}>
                    <div className="text-xl font-bold">{item._count.reports}</div>
                    <div className="text-xs uppercase tracking-wider font-semibold">Reports</div>
                  </div>
                  <button 
                    onClick={() => handleViewReports(item)}
                    className="p-2 bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors border"
                  >
                    <FiEye className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reports Detail Modal */}
      {isModalOpen && selectedProject &&
        <div className="fixed inset-0 z-[100] overflow-y-auto flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-neutral-900/90 backdrop-blur-md transition-opacity" 
            onClick={() => setIsModalOpen(false)}
          />

          <div 
            className="relative z-[110] bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiAlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-xl leading-6 font-bold text-gray-900">
                      Reports for {selectedProject.title}
                    </h3>
                    
                    <div className="mt-6 space-y-4 max-h-96 overflow-y-auto pr-2">
                        {loadingReports ? (
                            <div className="flex justify-center py-8">
                                <FiLoader className="h-6 w-6 text-blue-600 animate-spin" />
                            </div>
                        ) : projectReports.length === 0 ? (
                            <p className="text-gray-500 text-center py-4 italic">No report details available.</p>
                        ) : (
                            projectReports.map((report) => (
                                <div key={report.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-gray-900">{report.reason}</span>
                                            <span className="text-xs text-gray-500">Reported by: {report.user?.full_name || report.user?.pi_username || 'Anonymized User'}</span>
                                        </div>
                                        <span className="text-xs text-gray-400">{new Date(report.created_at).toLocaleString()}</span>
                                    </div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{report.details}</p>
                                </div>
                            ))
                        )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button 
                  type="button" 
                  disabled={processing}
                  onClick={() => handleReactivateProject(selectedProject.id)}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {processing ? (
                    <FiLoader className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FiCheckCircle className="mr-2 h-4 w-4" />
                  )}
                  Reactivate Project
                </button>
                <button 
                  type="button" 
                  disabled={processing}
                  onClick={() => handleDismissReports(selectedProject.id)}
                  className="mt-3 w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {processing ? (
                    <FiLoader className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FiTrash2 className="mr-2 h-4 w-4" />
                  )}
                  Dismiss Reports
                </button>
                <button 
                  type="button" 
                  disabled={processing}
                  onClick={() => handleSuspendProject(selectedProject.id)}
                  className="mt-3 w-full inline-flex justify-center items-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Hide Project
                </button>
                <button 
                  type="button" 
                  disabled={processing}
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
          </div>
        </div>
      }
    </div>
  );
};

export default ReportsPage;
